import { makeStyles } from '@material-ui/core/styles';
import { redesignTheme } from '../../theme';
import { primaryButtonStyle, secondaryButtonStyle } from '../styles/buttons.styles';
import { formFieldStyles } from '../styles/formField.styles';

const { colors, fonts, fontSize, spacing, radius, breakpoints } = redesignTheme;

export const useInlineSignupStyles = makeStyles(() => ({
  card: {
    borderRadius: 0,
    padding: 40,
    backgroundColor: colors.white,
    boxShadow: '0 8px 32px rgba(35,41,111,0.10)',
    margin: '20px auto 0 auto',
    maxWidth: 900,
    width: '100%',
    boxSizing: 'border-box' as const,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      padding: spacing.sm,
      margin: 0,
    },
  },
  layout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing.lg,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      flexDirection: 'column' as const,
      gap: 20,
    },
  },
  imageWrapper: {
    flex: '0 0 40%',
    borderRadius: radius.sm,
    overflow: 'hidden',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      flex: '1 1 100%',
      maxHeight: 240,
      width: '100%',
    },
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
    minHeight: 280,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      minHeight: 180,
      maxHeight: 240,
    },
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
  },
  title: {
    margin: `0 0 ${spacing.xs}px`,
    fontFamily: fonts.gothicSemiBold,
    fontSize: fontSize.lg,
    color: colors.primary,
    lineHeight: 'normal',
  },
  description: {
    margin: `0 0 20px`,
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    lineHeight: 1.6,
    color: colors.chatMessageText,
  },
  stepIndicator: {
    marginBottom: 18,
  },
  stepLink: {
    margin: `0 0 20px`,
    fontFamily: fonts.opensans,
    fontSize: fontSize.sm,
    color: colors.chatMessageText,
    textAlign: 'center' as const,
  },
  stepLinkBold: {
    fontFamily: fonts.opensansSemiBold,
    color: colors.primary,
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  steps: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: 'rgba(0,10,140,0.08)',
    color: colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.sm,
  },
  stepNumberInactive: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#E4E4E7',
    color: '#6B7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.sm,
  },
  stepCheckDone: {
    width: 26,
    height: 26,
    borderRadius: 6,
    backgroundColor: '#E4E4E7',
    color: colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.sm,
  },
  stepLabelActive: {
    margin: 0,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.sm,
    color: colors.primary,
  },
  stepLabel: {
    margin: 0,
    fontFamily: fonts.opensans,
    fontSize: fontSize.sm,
    color: colors.placeholder,
  },
  field: {
    ...formFieldStyles.field,
    marginBottom: 12,
  },
  label: {
    ...formFieldStyles.label,
  },
  input: {
    ...formFieldStyles.input,
  },
  inputWrapper: {
    ...formFieldStyles.inputWrapper,
  },
  inputWithIcon: {
    ...formFieldStyles.inputWithIcon,
  },
  inputIcon: {
    ...formFieldStyles.inputIcon,
  },
  materialPhoneInput: {
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    height: redesignTheme.sizes.inputHeight,
    backgroundColor: colors.white,
  },
  helperText: {
    margin: '4px 0 0',
    fontFamily: fonts.opensans,
    fontSize: fontSize.sm,
    color: '#6B7280',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
    marginBottom: 4,
  },
  checkbox: {
    padding: 0,
    '&.Mui-checked': {
      color: `${colors.primary} !important`,
    },
  },
  checkboxLabel: {
    margin: 0,
    fontFamily: fonts.opensans,
    fontSize: fontSize.sm,
    color: colors.chatMessageText,
    lineHeight: 1.5,
  },
  linkPrimary: {
    fontFamily: fonts.opensansSemiBold,
    color: colors.primary,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  forgotPassword: {
    fontFamily: fonts.opensans,
    fontSize: fontSize.sm,
    color: colors.link,
    textDecoration: 'underline',
    textAlign: 'right' as const,
    display: 'block',
    marginBottom: 12,
  },
  errorText: {
    ...formFieldStyles.errorField,
    marginTop: 2,
    marginBottom: 4,
  },
  submitButton: {
    ...primaryButtonStyle,
    width: '100%',
    textTransform: 'none' as const,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: colors.primaryDark,
      opacity: 0.92,
      boxShadow: 'none',
    },
    '&.Mui-disabled, &:disabled': {
      color: 'rgba(255,255,255,0.7)',
      backgroundColor: colors.primaryDark,
      opacity: 0.5,
    },
  },
  dividerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    width: '100%',
    margin: '16px 0',
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
  },
  dividerText: {
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    color: colors.textMuted,
    lineHeight: 1.5,
  },
  ssoSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
    width: '100%',
  },
  outlineButton: {
    ...secondaryButtonStyle,
    width: '100%',
    textTransform: 'none' as const,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: colors.hoverSurface,
      boxShadow: 'none',
    },
  },
  ssoButtonText: {
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    color: colors.primaryDark,
    letterSpacing: '0.28px',
    paddingLeft: spacing.xs,
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    color: colors.chatMessageText,
  },
  bottomLink: {
    margin: '16px 0 0',
    fontFamily: fonts.opensans,
    fontSize: fontSize.sm,
    color: colors.chatMessageText,
    textAlign: 'center' as const,
  },
  resultCard: {
    borderRadius: 0,
    padding: '64px 40px',
    backgroundColor: colors.white,
    boxShadow: '0 8px 32px rgba(35,41,111,0.10)',
    margin: '20px auto 0 auto',
    maxWidth: 900,
    width: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    minHeight: 420,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      padding: '48px 16px',
      margin: 0,
      minHeight: 360,
    },
  },
  resultIconWrapper: {
    width: 56,
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.accentMuted,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  resultIcon: {
    color: colors.primary,
    fontSize: 32,
  },
  resultTitle: {
    margin: `0 0 12px`,
    fontFamily: fonts.gothicSemiBold,
    fontSize: fontSize.xl,
    color: colors.primary,
  },
  resultDescription: {
    margin: `0 0 28px`,
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    lineHeight: 1.6,
    color: colors.chatMessageText,
    maxWidth: 380,
  },
  resultButton: {
    ...primaryButtonStyle,
    minWidth: 320,
    textTransform: 'none' as const,
    boxShadow: 'none',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      minWidth: '100%',
    },
  },
  confirmCard: {
    borderRadius: 0,
    padding: '48px 40px',
    backgroundColor: colors.white,
    boxShadow: '0 8px 32px rgba(35,41,111,0.10)',
    margin: '20px auto 0 auto',
    maxWidth: 900,
    width: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      padding: '32px 16px',
      margin: 0,
    },
  },
  smsErrorText: {
    fontFamily: fonts.opensans,
    fontSize: fontSize.sm,
    color: colors.error,
    textAlign: 'center' as const,
    marginTop: 12,
    marginBottom: 0,
  },
}));
